import { useQueryClient } from '@tanstack/react-query';
import { Smartphone } from 'lucide-react';
import { useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { register } from '@/domains/auth/apis/auth';
import OtpVerificationCard from '@/domains/auth/components/OtpVerificationCard/OtpVerificationCard';
import usePhoneVerification from '@/domains/auth/hooks/usePhoneVerification';
import { usePendingAuthStore } from '@/domains/auth/store/pendingAuthStore';
import { apiErrorMessage } from '@/domains/auth/utils/apiError';
import { SIGNUP_ROUTE } from '@/domains/auth/utils/authRoutes';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import { DEFAULT_LANDING_ROUTE } from '@/shared/utils/loginRedirect';
import { showToast } from '@/shared/utils/showToast';
import { useAuthStore } from '@/store/authStore';

// Second step of Buat Akun: Buat Akun sent the code and left the form in
// usePendingAuthStore; once the code matches, the account is created and signed in.
const Verification = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const registration = usePendingAuthStore((state) => state.registration);
  const { turnstileRef, sent, send, verify, resendIn, isSending, isVerifying, error, setError } =
    usePhoneVerification();
  const [code, setCode] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  // Set once signed in, so clearing the pending form does not send the user back to Buat Akun
  const finished = useRef(false);

  if (finished.current) return null;
  if (!registration || sent?.purpose !== 'register') {
    return <Navigate to={SIGNUP_ROUTE} replace />;
  }

  const onResend = async () => {
    try {
      await send({ purpose: 'register', ...registration });
      setCode('');
    } catch (err) {
      setError(apiErrorMessage(err, 'Kode tidak dapat dikirim. Silakan coba lagi.'));
    }
  };

  const onSubmit = async () => {
    if (!(await verify(code))) return;
    setIsRegistering(true);
    try {
      const profile = await register(registration);
      finished.current = true;
      usePendingAuthStore.getState().clear();
      useAuthStore.getState().setAuth(profile);
      queryClient.clear();
      showToast({ type: 'success', title: 'Akun dibuat', message: 'Selamat datang!' });
      navigate(DEFAULT_LANDING_ROUTE, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Pendaftaran gagal.'));
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <main className="h-screen w-full overflow-hidden justify-between bg-[linear-gradient(180deg,#FFF_0%,#007AFF_250.56%)]">
      <HeaderMobile className="bg-transparent!">
        <TopBarHeader type="back" title="Verifikasi" onBack={() => navigate(SIGNUP_ROUTE)} />
      </HeaderMobile>

      <WrapperMobile className="flex-1 overflow-auto m-auto h-full">
        <OtpVerificationCard
          icon={<Smartphone className="size-8 text-black" aria-hidden="true" />}
          title="Verifikasi Nomor HP"
          phone={sent.phone}
          channel={sent.channel}
          code={code}
          onCodeChange={setCode}
          onSubmit={onSubmit}
          onResend={onResend}
          resendIn={resendIn}
          error={error}
          isSending={isSending}
          isSubmitting={isVerifying || isRegistering}
        >
          <div ref={turnstileRef} className="flex justify-center empty:hidden" />
        </OtpVerificationCard>
      </WrapperMobile>
    </main>
  );
};

export default Verification;
